import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { Incident, IncidentInput, SEVERITY_OPTIONS } from '../types/incident';
import { initialCaseTypes } from '../data/caseTypes';
import { initialAgencies } from '../data/agencies';
import { states, getRegionsByState, getDistrictsByRegion } from '../data/geography';
interface IncidentFormProps {
  incident?: Incident;
  onSave: (data: IncidentInput) => void;
  onCancel: () => void;
}
export function IncidentForm({
  incident,
  onSave,
  onCancel
}: IncidentFormProps) {
  const [formData, setFormData] = useState<IncidentInput>({
    caseTypeId: incident?.caseTypeId || '',
    date: incident?.date || new Date().toISOString().split('T')[0],
    stateId: incident?.stateId || '',
    regionId: incident?.regionId || '',
    districtId: incident?.districtId || '',
    location: incident?.location || '',
    agencyId: incident?.agencyId || '',
    description: incident?.description || '',
    numCases: incident?.numCases || 1,
    numDeaths: incident?.numDeaths || 0,
    numInjuries: incident?.numInjuries || 0,
    severity: incident?.severity || 'Medium'
  });
  const [regions, setRegions] = useState(incident ? getRegionsByState(incident.stateId) : []);
  const [districts, setDistricts] = useState(incident ? getDistrictsByRegion(incident.stateId, incident.regionId) : []);
  useEffect(() => {
    if (formData.stateId) {
      setRegions(getRegionsByState(formData.stateId));
    } else {
      setRegions([]);
    }
  }, [formData.stateId]);
  useEffect(() => {
    if (formData.stateId && formData.regionId) {
      setDistricts(getDistrictsByRegion(formData.stateId, formData.regionId));
    } else {
      setDistricts([]);
    }
  }, [formData.stateId, formData.regionId]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      // Reset dependent fields
      if (name === 'stateId') {
        newData.regionId = '';
        newData.districtId = '';
      } else if (name === 'regionId') {
        newData.districtId = '';
      }

      // Parse numbers
      if (['numCases', 'numDeaths', 'numInjuries'].includes(name)) {
        newData[name as keyof IncidentInput] = parseInt(value) || 0;
      }
      return newData;
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="space-y-6 max-w-4xl mx-auto pb-12" data-id="element-284" data-mp="16ed997lcnye5">
      <div className="flex items-center gap-4" data-id="element-285" data-mp="12b727xqey36m">
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-286" data-mp="12e30jn66atvl">
          <ArrowLeft size={20} className="text-gray-600" data-id="element-287" data-mp="xezy8ahkv1rb" />
        </button>
        <div data-id="element-288" data-mp="3n3sd1tjem3l4">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-289" data-mp="3n04jiudylh6e">{incident ? 'Edit Incident' : 'Report New Incident'}</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-290" data-mp="3i5h21fhdhn1b">Fill in the details below to register a security incident</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-291" data-mp="22jdlb5p2eqct">
        <div className="p-6 space-y-8" data-id="element-292" data-mp="26kysqbitycfm">
          
          {/* Basic Info */}
          <div data-id="element-293" data-mp="2bcqn6x53x2b1">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 mb-4" data-id="element-294" data-mp="2bda2e63dhcld">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-295" data-mp="2gbbn0vgg5jyy">
              <div data-id="element-296" data-mp="2l9liz89zwolm">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-297" data-mp="2l93ejod6j42m">Incident Type <span className="text-red-500" data-id="element-298" data-mp="2qasqd6887lrr">*</span></label>
                <select required name="caseTypeId" value={formData.caseTypeId} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-299" data-mp="2ub2uedcgk23z">
                  <option value="" data-id="element-300" data-mp="zxle9bedu7iz">Select Type</option>
                  {initialCaseTypes.filter(t => t.status === 'Active').map(t => <option key={t.id} value={t.id} data-id="element-301" data-mp="ltkfobv837y">{t.name}</option>)}
                </select>
              </div>
              <div data-id="element-302" data-mp="ek9ot8m99hmx">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-303" data-mp="ekjjh9i8wcyy">Date <span className="text-red-500" data-id="element-304" data-mp="9lyekws1t546">*</span></label>
                <input required type="date" name="date" value={formData.date} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-305" data-mp="3i21eggcx5phy" />
              </div>
              <div data-id="element-306" data-mp="3rtxqkvbubg56">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-307" data-mp="3n14lu7gdg7t3">Reporting Agency <span className="text-red-500" data-id="element-308" data-mp="3mz1nn052f8da">*</span></label>
                <select required name="agencyId" value={formData.agencyId} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-309" data-mp="2za8wdgze1op0">
                  <option value="" data-id="element-310" data-mp="1ekv31hz4lv52">Select Agency</option>
                  {initialAgencies.filter(a => a.status === 'Active').map(a => <option key={a.id} value={a.id} data-id="element-311" data-mp="10v1cmv7z32t8">{a.name}</option>)}
                </select>
              </div>
              <div data-id="element-312" data-mp="14t8xa15k4o9p">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-313" data-mp="1skwc3kpjo8vm">Severity <span className="text-red-500" data-id="element-314" data-mp="1sitfghtvzhhx">*</span></label>
                <select required name="severity" value={formData.severity} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-315" data-mp="1xixyofm6iqbu">
                  {SEVERITY_OPTIONS.map(s => <option key={s} value={s} data-id="element-316" data-mp="849n2glexcev">{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div data-id="element-317" data-mp="2etl1pzzygum4">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 mb-4" data-id="element-318" data-mp="2es07kbhjzdos">Location Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-319" data-mp="29v0aur6lfnac">
              <div data-id="element-320" data-mp="252gph81nznuz">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-321" data-mp="251p22ki80wfs">State <span className="text-red-500" data-id="element-322" data-mp="210ve5r4s3hmw">*</span></label>
                <select required name="stateId" value={formData.stateId} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-323" data-mp="1w2txfcutruel">
                  <option value="" data-id="element-324" data-mp="1m55zke1xa6s7">Select State</option>
                  {states.map(s => <option key={s.id} value={s.id} data-id="element-325" data-mp="1i3u9k7f4zjjh">{s.name}</option>)}
                </select>
              </div>
              <div data-id="element-326" data-mp="3ghndgv0vfuk6">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-327" data-mp="3ghvv107xvo1p">Region <span className="text-red-500" data-id="element-328" data-mp="3ci4vo84cjzlk">*</span></label>
                <select required name="regionId" value={formData.regionId} onChange={handleChange} disabled={!formData.stateId} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" data-id="element-329" data-mp="y9c2wehx65x2">
                  <option value="" data-id="element-330" data-mp="1c0z48sqpy4mp">Select Region</option>
                  {regions.map(r => <option key={r.id} value={r.id} data-id="element-331" data-mp="faq2z2y52kuq">{r.name}</option>)}
                </select>
              </div>
              <div data-id="element-332" data-mp="k91erj7zv5fl">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-333" data-mp="kacf1928dv1y">District <span className="text-red-500" data-id="element-334" data-mp="oamxhl77mdxp">*</span></label>
                <select required name="districtId" value={formData.districtId} onChange={handleChange} disabled={!formData.regionId} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" data-id="element-335" data-mp="od949ima0pev">
                  <option value="" data-id="element-336" data-mp="1gcb41lufi9m2">Select District</option>
                  {districts.map(d => <option key={d.id} value={d.id} data-id="element-337" data-mp="1bdq3flb9lnx3">{d.name}</option>)}
                </select>
              </div>
            </div>
            <div data-id="element-338" data-mp="1p5eb9cehdq89">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-339" data-mp="1p5xucwfvaknv">Specific Location <span className="text-red-500" data-id="element-340" data-mp="1ke5zwatlbusg">*</span></label>
              <input required type="text" name="location" value={formData.location} onChange={handleChange} placeholder="e.g., Main Market, Highway Checkpoint" className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-341" data-mp="1kclaezm4v21g" />
            </div>
          </div>

          {/* Impact & Description */}
          <div data-id="element-342" data-mp="240kke88vi55t">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 mb-4" data-id="element-343" data-mp="dppp2n91wv8i">Impact & Description</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-344" data-mp="9p5t3n07rxny">
              <div data-id="element-345" data-mp="3vptqmevap2a2">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-346" data-mp="3vpl9ubw1xctl">Number of Cases</label>
                <input type="number" min="1" name="numCases" value={formData.numCases} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-347" data-mp="3mans8bsqir6" />
              </div>
              <div data-id="element-348" data-mp="3c1cho1uid6u3">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-349" data-mp="3c0sxsflas8df">Number of Deaths</label>
                <input type="number" min="0" name="numDeaths" value={formData.numDeaths} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-350" data-mp="3h1qh4vi1n8gi" />
              </div>
              <div data-id="element-351" data-mp="11aujolzqzxt9">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-352" data-mp="11acc4t7mxx61">Number of Injuries</label>
                <input type="number" min="0" name="numInjuries" value={formData.numInjuries} onChange={handleChange} className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-353" data-mp="1684e6g1zuzny" />
              </div>
            </div>
            <div data-id="element-354" data-mp="n6p31mps64l4">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-355" data-mp="radbe7m8l47f">Incident Description <span className="text-red-500" data-id="element-356" data-mp="r80sns8nmptb">*</span></label>
              <textarea required name="description" value={formData.description} onChange={handleChange} rows={4} placeholder="Provide a detailed description of the incident..." className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-357" data-mp="1f196bdlzofiw"></textarea>
            </div>
          </div>

        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3" data-id="element-358" data-mp="3qoh21l2c3ost">
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors" data-id="element-359" data-mp="3vpd4rvay94wn">
            Cancel
          </button>
          <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded-md hover:bg-blue-900 transition-colors" data-id="element-360" data-mp="3j5pwb9itl6lp">
            <Save size={16} data-id="element-361" data-mp="3s7pdc1tu4aon" />
            {incident ? 'Save Changes' : 'Submit Incident'}
          </button>
        </div>
      </form>
    </div>;
}